/**
 * Balance Sheet — /finance/reports/balance-sheet
 *
 * Assets = Liabilities + Equity at a chosen date.
 * Calls GET /finance/reports/balance-sheet?asOf=YYYY-MM-DD
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Scale, RefreshCw, Download, CheckCircle2, AlertCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadAuthenticatedFile } from './report-utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BsData {
  reportType: string;
  asOf: string;
  assets:      { total: number };
  liabilities: { total: number };
  equity:      { retainedEarnings: number; total: number };
  balanced: boolean;
  breakdown: Record<string, { debit: number; credit: number; balance: number }>;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function BsSection({ title, amount, sub, color }: { title: string; amount: number; sub?: string; color: string }) {
  return (
    <div className={`rounded-lg p-4 border ${color}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{title}</div>
      {sub && <div className="text-xs text-muted-foreground mb-1">{sub}</div>}
      <div className="text-2xl font-bold font-mono">{fmt(amount)}</div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function BalanceSheetPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState<BsData | null>(null);
  const [asOf, setAsOf]       = useState(new Date().toISOString().split('T')[0]!);
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (d: string) => {
    setLoading(true);
    try {
      const res = await api.get<BsData>(`/finance/reports/balance-sheet?asOf=${d}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadedRef.current = false;
    fetchData(asOf);
  }, [fetchData, asOf]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(asOf);
  }, [fetchData, asOf]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Scale className="h-6 w-6 text-purple-600" />
          <div>
            <h1 className="text-xl font-semibold">Balance Sheet</h1>
            <p className="text-sm text-muted-foreground">Assets, liabilities, and equity as at {asOf}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={asOf} onChange={e => { setAsOf(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadAuthenticatedFile(`/finance/reports/balance-sheet/export.xlsx?asOf=${asOf}`, `balance-sheet-${asOf}.xlsx`)}>
            <Download className="h-4 w-4 mr-2" /> XLSX
          </Button>
        </div>
      </div>

      {/* Balanced banner */}
      {data && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
          data.balanced ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {data.balanced
            ? <><CheckCircle2 className="h-4 w-4" /> Balanced — Assets = Liabilities + Equity</>
            : <><AlertCircle className="h-4 w-4" /> UNBALANCED — check ledger entries</>}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : !data ? (
        <div className="text-center py-12 text-muted-foreground">No data found.</div>
      ) : (
        <>
          {/* Three-column summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BsSection title="Total Assets"      amount={data.assets.total}      color="bg-blue-50 border-blue-200" />
            <BsSection title="Total Liabilities" amount={data.liabilities.total} color="bg-orange-50 border-orange-200" />
            <BsSection
              title="Total Equity"
              amount={data.equity.total}
              sub={`Retained Earnings: ${fmt(data.equity.retainedEarnings)}`}
              color="bg-purple-50 border-purple-200"
            />
          </div>

          {/* Equation check */}
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-3">Accounting Equation</p>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Assets</TableCell>
                    <TableCell className="text-right font-mono">{fmt(data.assets.total)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-muted-foreground pl-4">Liabilities</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{fmt(data.liabilities.total)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-muted-foreground pl-4">Equity (incl. retained earnings)</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{fmt(data.equity.total)}</TableCell>
                  </TableRow>
                  <TableRow className="font-semibold bg-muted/30">
                    <TableCell className="pl-4">Liabilities + Equity</TableCell>
                    <TableCell className={`py-2 text-right font-mono ${
                      data.balanced ? 'text-green-700' : 'text-red-600'
                    }`}>{fmt(data.liabilities.total + data.equity.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Account-type breakdown */}
          {Object.keys(data.breakdown).length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-3">Breakdown by Account Type</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(data.breakdown).map(([type, v]) => (
                      <TableRow key={type} className="border-t">
                        <TableCell className="capitalize">{type}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmt(v.debit)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmt(v.credit)}</TableCell>
                        <TableCell className={`py-2 text-right font-mono text-xs font-medium ${
                          v.balance >= 0 ? 'text-blue-700' : 'text-red-600'
                        }`}>{fmt(v.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

